import { getAccessToken } from '../authentication/supabaseService';
import { fetchOrderHistoryByPlatformIds, fetchOrderHistoryDateBounds } from '../orderhistory/orderhistoryService';
import type { OrderHistory } from '../../stores/appOrderHistoryStore';
import type { SourcingRunRequest, SourcingRunResponse } from './sourcingRunTypes';
import type { SourcingPeriod } from './sourcingRunCalculations';

export async function fetchSourcingRun(request: SourcingRunRequest): Promise<SourcingRunResponse> {
  const token = await getAccessToken();
  const response = await fetch('/api/sourceOptions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || error.error || 'Failed to load sourcing results');
  }
  const result = await response.json() as SourcingRunResponse;
  if (result.version !== 2) throw new Error('Unexpected sourcing response version');
  return result;
}

/** Bounded canonical order reads, isolated from the Products page's mutable selection. */
export async function fetchSourcingVolumeOrders(platformIdsByRestaurant: Record<string, string[]>, period: SourcingPeriod): Promise<OrderHistory[]> {
  const restaurantByPlatform = new Map<string, string>();
  for (const [restaurant, platforms] of Object.entries(platformIdsByRestaurant)) for (const platform of platforms) restaurantByPlatform.set(platform, restaurant);
  if (!restaurantByPlatform.size) return [];
  let start = new Date(period.start), end = new Date(period.end);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || start > end) throw new Error('Invalid sourcing purchase period');
  // “All time” may begin decades before this account existed. Skip empty years.
  if (end.getTime() - start.getTime() > 183 * 86400000) {
    const bounds = await fetchOrderHistoryDateBounds(Array.from(restaurantByPlatform.keys()));
    if (!bounds.minDate || !bounds.maxDate) return [];
    start = new Date(Math.max(start.getTime(), bounds.minDate.getTime()));
    end = new Date(Math.min(end.getTime(), bounds.maxDate.getTime()));
  }
  const orders = new Map<string, OrderHistory>();
  let cursor = start;
  while (cursor <= end) {
    const chunkEnd = new Date(cursor);
    chunkEnd.setUTCMonth(chunkEnd.getUTCMonth() + 6);
    const until = new Date(Math.min(chunkEnd.getTime() - 1, end.getTime()));
    const chunk = await fetchOrderHistoryByPlatformIds(Array.from(restaurantByPlatform.keys()), cursor, until);
    for (const order of chunk) {
      const restaurantId = restaurantByPlatform.get(order.restaurantPlatformId);
      if (restaurantId) orders.set(order.id, { ...order, restaurantId });
    }
    cursor = new Date(until.getTime() + 1);
  }
  return Array.from(orders.values());
}
